import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
  getWebForm,
  createWebForm,
  updateWebForm,
  listWebFormDomains,
  addWebFormDomain,
  removeWebFormDomain
} from "../../services/webForms.api";
import { uploadSingleFile } from "../../services/files.api";
import { fieldCatalogLabel } from "../../constants/webFormFields";

const BACKEND_URL =
  window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;

const EMPTY = {
  name: "",
  fieldsJson: [],
  styleJson: {},
  queueId: "",
  tagName: "",
  redirectUrl: "",
  redirectButtonLabel: "",
  ctaMode: "url",
  autoRedirect: false,
  confirmationMessage: "",
  status: "activo"
};

// Hook custom + estado plano, sin Formik — mismo estilo que
// `useWabaTemplateCreate.js`. Vive DENTRO de un modal (doc 0417 §8 corregido:
// el patrón de "Conexiones" es modal, no página — mismo criterio que
// WhatsAppModal/WhatsAppOficialModal/WhatsAppFacebookModal), así que no
// conoce `history`: crear un formulario nuevo NO navega a ningún lado, solo
// pasa de "nuevo" a "editando" sin cerrar el modal.
export function useWebFormBuilder({ webFormId, open }) {
  const [currentId, setCurrentId] = useState(webFormId || null);
  const [form, setForm] = useState(EMPTY);
  const [hash, setHash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [savingDomain, setSavingDomain] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isEditing = !!currentId;

  // Se re-arma cada vez que el modal se ABRE — mismo criterio que
  // `WhatsAppModal` (que fuerza remount de Formik por `key={whatsAppId}`):
  // sin esto, reabrir el modal para "Nuevo" después de haber editado otro
  // formulario arrastraría el estado del anterior.
  useEffect(() => {
    if (!open) return;
    setCurrentId(webFormId || null);
    setHash(null);
    setDomains([]);
    setNewDomain("");

    if (!webFormId) {
      setForm(EMPTY);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const { data } = await getWebForm(webFormId);
        setForm({
          name: data.name ?? "",
          fieldsJson: Array.isArray(data.fieldsJson) ? data.fieldsJson : [],
          styleJson: data.styleJson ?? {},
          queueId: data.queueId ?? "",
          tagName: data.tagName ?? "",
          redirectUrl: data.redirectUrl ?? "",
          redirectButtonLabel: data.redirectButtonLabel ?? "",
          ctaMode: data.ctaMode === "whatsapp" ? "whatsapp" : "url",
          autoRedirect: !!data.autoRedirect,
          confirmationMessage: data.confirmationMessage ?? "",
          status: data.status ?? "activo"
        });
        setHash(data.hash ?? null);
        const domainsRes = await listWebFormDomains(webFormId);
        setDomains(domainsRes.data ?? []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, webFormId]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const setStyle = (field, value) =>
    setForm(p => ({ ...p, styleJson: { ...p.styleJson, [field]: value } }));

  const addField = tipo =>
    setForm(p => ({
      ...p,
      fieldsJson: [
        ...p.fieldsJson,
        { tipo, etiqueta: fieldCatalogLabel(tipo), obligatorio: false, orden: p.fieldsJson.length + 1 }
      ]
    }));

  const updateField = (index, patch) =>
    setForm(p => {
      const arr = [...p.fieldsJson];
      arr[index] = { ...arr[index], ...patch };
      return { ...p, fieldsJson: arr };
    });

  const removeField = index =>
    setForm(p => {
      const arr = p.fieldsJson.filter((_, i) => i !== index);
      return { ...p, fieldsJson: arr.map((f, i) => ({ ...f, orden: i + 1 })) };
    });

  const reorderFields = (fromIndex, toIndex) =>
    setForm(p => {
      if (fromIndex === toIndex) return p;
      const arr = [...p.fieldsJson];
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, moved);
      return { ...p, fieldsJson: arr.map((f, i) => ({ ...f, orden: i + 1 })) };
    });

  const error = (() => {
    if (!form.name.trim()) return "El formulario necesita un nombre.";
    if (form.fieldsJson.length === 0) return "Agrega al menos un campo.";
    return null;
  })();

  const buildPayload = () => ({
    name: form.name.trim(),
    fieldsJson: form.fieldsJson,
    styleJson: form.styleJson,
    queueId: form.queueId || null,
    tagName: form.tagName || null,
    redirectUrl: form.redirectUrl || null,
    redirectButtonLabel: form.redirectButtonLabel || null,
    ctaMode: form.ctaMode,
    autoRedirect: form.ctaMode === "url" ? form.autoRedirect : false,
    confirmationMessage: form.confirmationMessage || null
  });

  const handleSubmit = async onSaved => {
    if (error) {
      toast.info(error);
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await updateWebForm(currentId, { ...buildPayload(), status: form.status });
        toast.success("Formulario actualizado.");
      } else {
        const { data } = await createWebForm(buildPayload());
        setCurrentId(data.id);
        setHash(data.hash);
        toast.success("Formulario creado. Ya puedes copiar el script en «Instalación».");
      }
      if (onSaved) onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!isEditing) return;
    const nextStatus = form.status === "activo" ? "pausado" : "activo";
    setSaving(true);
    try {
      await updateWebForm(currentId, { ...buildPayload(), status: nextStatus });
      set("status", nextStatus);
      toast.success(nextStatus === "activo" ? "Formulario activado." : "Formulario pausado.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain || !currentId) return;
    setSavingDomain(true);
    try {
      await addWebFormDomain(currentId, domain);
      setDomains(p => (p.includes(domain) ? p : [...p, domain].sort()));
      setNewDomain("");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingDomain(false);
    }
  };

  const handleRemoveDomain = async domain => {
    if (!currentId) return;
    setSavingDomain(true);
    try {
      await removeWebFormDomain(currentId, domain);
      setDomains(p => p.filter(d => d !== domain));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingDomain(false);
    }
  };

  // Reutiliza el endpoint genérico /files/upload-single (ya usado por
  // GroupCampaignModal) — no se creó infraestructura nueva de subida, solo
  // esta pieza de wiring para el Builder. El path devuelto es relativo a
  // `public/`; se guarda la URL absoluta en `styleJson.logoUrl` para que
  // tanto la vista previa como `embed.js` la usen tal cual, sin adivinar el
  // origen del backend.
  const handleUploadLogo = async file => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await uploadSingleFile(formData);
      setStyle("logoUrl", `${BACKEND_URL}/public/${data.path}`);
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => setStyle("logoUrl", null);

  return {
    isEditing,
    loading,
    form,
    set,
    setStyle,
    addField,
    updateField,
    removeField,
    reorderFields,
    error,
    saving,
    handleSubmit,
    toggleStatus,
    hash,
    domains,
    newDomain,
    setNewDomain,
    savingDomain,
    handleAddDomain,
    handleRemoveDomain,
    uploadingLogo,
    handleUploadLogo,
    removeLogo
  };
}
